// src/components/widgets/WidgetPreview.jsx
import React, { useState, useMemo } from 'react';
import {
  Monitor,
  Smartphone,
  Save
} from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../utils/cn';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../hooks/useSettings';

// Import widget component and provider from widget-source
import BookingWidget from '@/widget-source/widgets/booking/BookingWidget';
import { DashboardBookingProvider } from '@/widget-source/widgets/booking/DashboardBookingProvider';

// Import widget styles
import '@/widget-source/styles/globals.css';

// Import CSS scoping styles
import '../../styles/widget-preview-scope.css';

const WidgetPreview = ({
  formData,
  previewState = {},
  onPreviewStateChange,
  onConfigChange,
  hasUnpublishedChanges = false,
  onPublish,
  publishingWidget = false,
  savingWidget = false,
  configurationMode = true,
  onConfigurationModeChange,
  showModeToggle = true,
  showControlBar = true,
  previewDevice,
  className = ''
}) => {
  const { currentUser } = useAuth();
  const { services: allServices } = useSettings();
  const [localPreviewMode, setLocalPreviewMode] = useState('desktop');

  // Use controlled device mode if provided, otherwise use local state
  const previewMode = previewDevice || localPreviewMode;
  const setPreviewMode = setLocalPreviewMode;

  // Device dimensions
  const previewDimensions = {
    desktop: { width: '100%', height: '100%' },
    mobile: { width: '375px', height: '100%' }
  };

  const currentDimensions = previewDimensions[previewMode];

  // Helper: Convert hex color to HSL format (for CSS variables)
  const hexToHSL = (hex) => {
    // Remove # if present
    hex = hex.replace(/^#/, '');

    // Parse hex to RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }

    h = Math.round(h * 360);
    s = Math.round(s * 100);
    l = Math.round(l * 100);

    return `${h} ${s}% ${l}%`;
  };

  // Prepare config for DashboardBookingProvider
  const widgetConfig = {
    businessApiKey: currentUser?.business?.api_key || 'preview-mode',
    apiBaseUrl: '/api/v2/public',
    widgetConfig: formData?.configuration || {},
    styling: {
      primaryColor: formData?.primary_color || '#007bff',
      theme: formData?.theme || 'auto',
      borderRadius: formData?.border_radius || 8
    },
    businessInfo: {
      id: currentUser?.business?.id || 'preview',
      name: currentUser?.business?.name || 'Your Business',
      slug: currentUser?.business?.slug || 'preview'
    }
  };

  // Get real services filtered by enabled_services configuration
  const widgetServices = useMemo(() => {
    if (!allServices || allServices.length === 0) {
      return [];
    }

    // Get enabled service IDs from widget configuration
    const enabledServiceIds = formData?.configuration?.enabled_services || [];

    // Filter to only enabled services
    const filteredServices = enabledServiceIds.length > 0
      ? allServices.filter(service => enabledServiceIds.includes(service.id))
      : allServices; // Show all if none specifically selected

    // Transform dashboard service structure to widget Service interface
    return filteredServices.map(service => ({
      id: service.id,
      name: service.name,
      description: service.description || '',
      base_duration: service.duration || 60,
      base_price: service.price ? String(service.price) : null,
      color_code: service.color || '#3b82f6',
      requires_confirmation: service.requires_confirmation || false,
      addons: [], // Dashboard services don't have addons yet
      price_range: {
        minimum: service.price ? String(service.price) : null,
        maximum: service.price ? String(service.price) : null,
        has_addons: false
      }
    }));
  }, [allServices, formData?.configuration?.enabled_services]);

  // Mock availability data for preview
  const mockAvailability = {
    '2025-10-31': ['09:00:00', '10:00:00', '14:00:00', '15:00:00'],
    '2025-11-01': ['10:00:00', '11:00:00', '13:00:00', '16:00:00'],
    '2025-11-03': ['09:00:00', '11:00:00', '14:00:00', '17:00:00'],
    '2025-11-04': ['10:00:00', '12:00:00', '15:00:00', '16:00:00'],
    '2025-11-05': ['09:00:00', '10:00:00', '13:00:00', '14:00:00'],
  };

  // Generate CSS variables from widget config (all in HSL format)
  const primaryColorHSL = formData?.primary_color ? hexToHSL(formData.primary_color) : '221.2 83.2% 53.3%';

  const widgetStyles = {
    '--primary': primaryColorHSL,
    '--background': formData?.theme === 'dark' ? '222.2 84% 4.9%' : '0 0% 100%',
    '--foreground': formData?.theme === 'dark' ? '210 40% 98%' : '222.2 84% 4.9%',
    '--card': formData?.theme === 'dark' ? '222.2 84% 4.9%' : '0 0% 100%',
    '--card-foreground': formData?.theme === 'dark' ? '210 40% 98%' : '222.2 84% 4.9%',
    '--popover': formData?.theme === 'dark' ? '222.2 84% 4.9%' : '0 0% 100%',
    '--popover-foreground': formData?.theme === 'dark' ? '210 40% 98%' : '222.2 84% 4.9%',
    '--primary-foreground': '210 40% 98%',
    '--secondary': formData?.theme === 'dark' ? '217.2 32.6% 17.5%' : '210 40% 96.1%',
    '--secondary-foreground': formData?.theme === 'dark' ? '210 40% 98%' : '222.2 47.4% 11.2%',
    '--muted': formData?.theme === 'dark' ? '217.2 32.6% 17.5%' : '210 40% 96.1%',
    '--muted-foreground': formData?.theme === 'dark' ? '215 20.2% 65.1%' : '215.4 16.3% 46.9%',
    '--accent': formData?.theme === 'dark' ? '217.2 32.6% 17.5%' : '210 40% 96.1%',
    '--accent-foreground': formData?.theme === 'dark' ? '210 40% 98%' : '222.2 47.4% 11.2%',
    '--destructive': formData?.theme === 'dark' ? '0 62.8% 30.6%' : '0 84.2% 60.2%',
    '--destructive-foreground': '210 40% 98%',
    '--border': formData?.theme === 'dark' ? '217.2 32.6% 17.5%' : '214.3 31.8% 91.4%',
    '--input': formData?.theme === 'dark' ? '217.2 32.6% 17.5%' : '214.3 31.8% 91.4%',
    '--ring': primaryColorHSL,
    '--radius': `${formData?.border_radius || 8}px`,
  };

  if (!formData) {
    return (
      <div className={cn("h-full flex items-center justify-center", className)}>
        <p className="text-muted-foreground">Select a widget to preview</p>
      </div>
    );
  }

  // Step navigation (only show in configuration mode)
  const steps = [
    { key: 'service', label: 'Service' },
    { key: 'datetime', label: 'Date & Time' },
    { key: 'contact', label: 'Contact' },
    { key: 'confirm', label: 'Confirm' },
    { key: 'success', label: 'Success' }
  ];

  const currentStep = previewState.currentStep || 'service';

  const handleStepClick = (stepKey) => {
    if (configurationMode && onPreviewStateChange) {
      onPreviewStateChange({ currentStep: stepKey });
    }
  };

  return (
    <div className={cn("h-full flex flex-col", className)}>
      {/* Control Bar - can be hidden when parent provides its own */}
      {showControlBar && (
        <div data-layer="administrative" className="flex-shrink-0 flex items-center justify-between gap-4 py-2 px-4 border-b bg-card">
          {/* Left Side: Device Selector + Mode Toggle + Step Navigation */}
          <div className="flex items-center gap-2">
            {/* Device Selector */}
            <div className="flex rounded-lg border p-1">
              <Button
                variant={previewMode === 'desktop' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPreviewMode('desktop')}
                className="h-7 w-7 p-0"
                title="Desktop view"
              >
                <Monitor className="w-4 h-4" />
              </Button>
              <Button
                variant={previewMode === 'mobile' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPreviewMode('mobile')}
                className="h-7 w-7 p-0"
                title="Mobile view"
              >
                <Smartphone className="w-4 h-4" />
              </Button>
            </div>

            {/* Mode Toggle */}
            {showModeToggle && (
              <div className="flex rounded-lg border p-1">
                <Button
                  variant={configurationMode ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => onConfigurationModeChange?.(true)}
                  className="h-7 px-3 text-xs"
                  title="Configuration mode - Jump to any step"
                >
                  Configure
                </Button>
                <Button
                  variant={!configurationMode ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => onConfigurationModeChange?.(false)}
                  className="h-7 px-3 text-xs"
                  title="Interactive mode - Test booking flow"
                >
                  Interactive
                </Button>
              </div>
            )}

            {/* Step Navigation - Only show in configuration mode */}
            {configurationMode && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Step:</span>
                <Select value={currentStep} onValueChange={handleStepClick}>
                  <SelectTrigger className="h-7 w-[140px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {steps.map(step => (
                      <SelectItem key={step.key} value={step.key} className="text-xs">
                        {step.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Right Side: Draft Status + Publish Button */}
          <div className="flex items-center gap-3">
            {/* Save Status - subtle text */}
            {savingWidget && (
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <div className="w-3 h-3 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                Saving...
              </span>
            )}

            {/* Draft Indicator - Plain text instead of Badge */}
            {hasUnpublishedChanges && !savingWidget && (
              <span className="text-xs text-muted-foreground">
                Unpublished changes
              </span>
            )}

            {/* Publish Button - only show when there are changes */}
            {hasUnpublishedChanges && (
              <Button
                onClick={onPublish}
                disabled={publishingWidget || savingWidget}
                size="sm"
                className="h-7"
              >
                {publishingWidget ? (
                  <>
                    <div className="w-3 h-3 border-2 border-background/30 border-t-background rounded-full animate-spin mr-1" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5 mr-1" />
                    Publish
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Preview Area */}
      <div data-layer="content" className="flex-1 bg-zinc-950 overflow-hidden p-4">
        <div className="h-full flex items-center justify-center">
          <div
            className="shadow-xl border transition-all duration-300 overflow-auto"
            style={{
              width: currentDimensions.width,
              height: currentDimensions.height,
              borderRadius: widgetStyles['--radius'],
              backgroundColor: `hsl(${widgetStyles['--background']})`
            }}
          >
            {/* Widget Preview Container with CSS Scoping */}
            <div
              className={cn(
                "widget-preview-scope widget-root h-full",
                formData.theme === 'dark' && 'dark'
              )}
              style={widgetStyles}
            >
              {/* Render widget with dashboard provider - FULLY CONTROLLED */}
              <DashboardBookingProvider
                services={widgetServices}
                availability={mockAvailability}
                config={widgetConfig}
                configurationMode={configurationMode}
                controlledState={previewState}
                onStateChange={onPreviewStateChange}
                onConfigChange={onConfigChange}
              >
                <BookingWidget />
              </DashboardBookingProvider>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetPreview;
